import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { FlexLayoutModule } from '@angular/flex-layout';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, take } from 'rxjs/operators';
import { interval } from 'rxjs';
import {
  ComponentBase,
  SharedService,
  BusinessDetailsService,
  REGISTRATION_PHASES_RESPONSE,
} from 'src/app/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { VideoComponent } from 'src/app/shared/video/video.component';
import { BusinessDetailsStepComponent } from '../registration-steps/business-details-step/business-details-step.component';
import { WorkingHoursStepComponent } from '../registration-steps/working-hours-step/working-hours-step.component';
import { AddEmployeesComponent } from './add-employees/add-employees.component';
import { TaxSetupComponent } from './tax-setup/tax-setup.component';
import { AddServicesComponent } from './add-services/add-services.component';
import { environment } from 'src/environments/environment';
import { StarLayerOverlayComponent } from './star-layer-overlay/star-layer-overlay.component';
import { AutoRegistrationTopBarComponent } from '../shared/auto-registration-top-bar/auto-registration-top-bar.component';

interface SuggestionItem {
  id: string;
  titleKey: string;
  descKey: string;
  actionKey: string;
  actionType: 'complete' | 'add';
  showInfo?: boolean;
  route?: string;
}

interface CarouselSlide {
  imageName: string;
  imageSrc: string;
}

@Component({
  selector: 'app-smart-suggestions',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FlexLayoutModule,
    AutoRegistrationTopBarComponent,
    MatSidenavModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    BusinessDetailsStepComponent,
    WorkingHoursStepComponent,
    AddEmployeesComponent,
    TaxSetupComponent,
    AddServicesComponent,
    VideoComponent,
    StarLayerOverlayComponent,
  ],
  templateUrl: './smart-suggestions.component.html',
  styleUrl: './smart-suggestions.component.scss',
})
export class SmartSuggestionsComponent extends ComponentBase implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('suggestionSidenav') suggestionSidenav!: MatSidenav;
  @ViewChild('businessDetailsCmp') businessDetailsCmp?: BusinessDetailsStepComponent;
  @ViewChild('workingHoursCmp') workingHoursCmp?: WorkingHoursStepComponent;
  @ViewChild('addEmployeesCmp') addEmployeesCmp?: AddEmployeesComponent;
  @ViewChild('addServicesCmp') addServicesCmp?: AddServicesComponent;
  @ViewChild('taxSetupCmp') taxSetupCmp?: TaxSetupComponent;

  /** Cached result from getRegisteredPhases (used for DoneBySkip checks). */
  phasesResult: REGISTRATION_PHASES_RESPONSE | null = null;

  /** Loader flag while calling getRegisteredPhases (controls welcome + cards skeletons). */
  phasesLoading = false;

  /** Which form to show in the sidenav. */
  sidenavContentType:
    | 'business-details'
    | 'working-hours'
    | 'add-employees'
    | 'add-services'
    | 'tax-setup'
    | null = null;

  userName = '';
  /** Total steps = 5 (all items in Complete Account section). */
  progressTotal = 5;

  /** Carousel slides rendered as image objects. */
  carouselSlides: CarouselSlide[] = [
    { imageName: 'Pro.png', imageSrc: 'assets/img/Pro.png' },
    { imageName: 'POS.png', imageSrc: 'assets/img/POS.png' },
    { imageName: 'One.png', imageSrc: 'assets/img/One.png' },
    { imageName: 'Kiosk.png', imageSrc: 'assets/img/Kiosk.png' },
    { imageName: 'business.png', imageSrc: 'assets/img/business.png' },
  ];
  carouselIndex = 0;
  showStarLayer = false;

  /** All 5 steps shown in "Complete Account" (progress bar = 5 steps). */
  completeAccountItems: SuggestionItem[] = [
    {
      id: 'business-details',
      titleKey: 'BUSINESS_DETAILS',
      descKey: 'Complete_business_details_description',
      actionKey: 'Complete',
      actionType: 'complete',
      route: 'choose-business',
    },
    {
      id: 'working-hours',
      titleKey: 'WORKING_HOUR',
      descKey: 'Set_working_hours_description',
      actionKey: 'Complete',
      actionType: 'complete',
      route: 'choose-business',
    },
    {
      id: 'add-employees',
      titleKey: 'Add_Employees',
      descKey: 'Add_employees_description',
      actionKey: 'Complete',
      actionType: 'complete',
      showInfo: true,
    },
    {
      id: 'add-services',
      titleKey: 'Add_Services',
      descKey: 'Add_services_description',
      actionKey: 'Complete',
      actionType: 'complete',
      showInfo: true,
    },
    {
      id: 'tax-setup',
      titleKey: 'Tax_setup',
      descKey: 'Tax_setup_description',
      actionKey: 'Complete',
      actionType: 'complete',
      showInfo: true,
    },
  ];

  /** Number of completed "Complete Account" steps (out of 5). */
  get progressCurrent(): number {
    return this.completeAccountItems.filter((item) => this.isItemDone(item)).length;
  }

  get progressPercent(): number {
    if (!this.progressTotal) return 0;
    return (this.progressCurrent / this.progressTotal) * 100;
  }

  /** Disabled state for sidenav Confirm button, mirroring each child component's logic. */
  get sidenavConfirmDisabled(): boolean {
    switch (this.sidenavContentType) {
      case 'business-details':
        return !this.businessDetailsCmp || !this.businessDetailsCmp.canSubmit || !!this.businessDetailsCmp.loading;
      case 'working-hours':
        // Working hours step validates on submit internally; we only block while loading.
        return !this.workingHoursCmp || !!this.workingHoursCmp.loading;
      case 'add-employees':
        return !this.addEmployeesCmp || !this.addEmployeesCmp.canConfirm || !!this.addEmployeesCmp.loading;
      case 'add-services':
        return !this.addServicesCmp || !this.addServicesCmp.canConfirm || !!this.addServicesCmp.loading;
      case 'tax-setup':
        return !this.taxSetupCmp || !this.taxSetupCmp.canConfirm || !!this.taxSetupCmp.loading;
      default:
        return true;
    }
  }

  /** Loading state for sidenav Confirm button, to show the same spinner UX as children. */
  get sidenavConfirmLoading(): boolean {
    switch (this.sidenavContentType) {
      case 'business-details':
        return !!this.businessDetailsCmp?.loading;
      case 'working-hours':
        return !!this.workingHoursCmp?.loading;
      case 'add-employees':
        return !!this.addEmployeesCmp?.loading;
      case 'add-services':
        return !!this.addServicesCmp?.loading;
      case 'tax-setup':
        return !!this.taxSetupCmp?.loading;
      default:
        return false;
    }
  }

  getSetupInfoKey(item: SuggestionItem): string {
    if (item.id === 'add-employees') {
      return 'Add_Employees_Info';
    }
    if (item.id === 'add-services') {
      return 'Add_Services_Info';
    }
    if (item.id === 'tax-setup') {
      return 'Tax_setup_Info';
    }
    return '';
  }

  constructor(
    private readonly sharedService: SharedService,
    private readonly businessDetailsService: BusinessDetailsService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    readonly translateService: TranslateService
  ) {
    super();
  }

  onOpenVideo(): void {
    this.dialog.open(VideoComponent, {
      minWidth: '60%',
      maxWidth: '100%',
      panelClass: 'aspect_ratio',
    });
  }

  lang: string = 'en';
  factorTranslateValue(index) {
    if (this.lang == 'en') {
      return 'translateX(-' + index * 100 + '%)';
    } else {
      return 'translateX(' + index * 100 + '%)';
    }
  }
  ngOnInit(): void {
    this.lang = this.sharedService.getLocalStorage('lang') || 'en';
    this.translateService.setDefaultLang(this.lang);
    this.translateService.use(this.lang).subscribe();
    this.loadUserData();
    this.startCarouselAutoSlide();
  }
  
  ngAfterViewInit(): void {
    this.startIntroSoundAndLayer();
  }

  private startIntroSoundAndLayer(): void {
    this.showStarLayer = true;
    this.sharedService.playSound('assets/sounds/smart-suggestion.mp3');
    setTimeout(() => this.showStarLayer = false, 3000);
  }

  private loadUserData(): void {
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    if (!regId) return;
    this.phasesLoading = true;
    this.businessDetailsService
      .getRegisteredPhases({ RegistrationCycleLogId: +regId })
      .pipe(
        take(1),
        finalize(() => (this.phasesLoading = false))
      )
      .subscribe((res) => {
        this.phasesResult = res?.Result ?? null;
        if (res?.Result?.One?.OneData?.FirstName) {
          this.userName = res.Result.One.OneData.FirstName;
        }
      });
  }

  /** Auto-advance carousel every 10 seconds. */
  private startCarouselAutoSlide(): void {
    interval(10000)
      .pipe(take(1))
      .subscribe(() => {
        if (!this.carouselSlides || this.carouselSlides.length === 0) return;
        const total = this.carouselSlides.length;
        this.carouselIndex = (this.carouselIndex + 1) % total;
        this.startCarouselAutoSlide();
      });
  }

  /** True if this item is done (used for progress + card checkmark). */
  isItemDone(item: SuggestionItem): boolean {
    return this.isCompleteAccountItemDone(item) || this.isCompleteSetupItemDone(item);
  }

  /** True if the Complete button for this item should show checkmark and be disabled. */
  isCompleteAccountItemDone(item: SuggestionItem): boolean {
    if (!this.phasesResult) return false;
    if (item.titleKey == 'BUSINESS_DETAILS') {
      return this.phasesResult.Three?.DoneBySkip == false;
    }
    if (item.titleKey == 'WORKING_HOUR') {
      return this.phasesResult.Four?.DoneBySkip == false;
    }
    return false;
  }

  /** True if the Add button for this setup item should show checkmark and be disabled. */
  isCompleteSetupItemDone(item: SuggestionItem): boolean {
    if (!this.phasesResult) return false;
    if (item.titleKey == 'Add_Employees') {
      return this.phasesResult.Five?.DoneBySkip == false;
    }
    if (item.titleKey == 'Add_Services') {
      return this.phasesResult.Six?.DoneBySkip == false;
    }
    if (item.titleKey == 'Tax_setup') {
      return this.phasesResult.Seven?.DoneBySkip == false;
    }
    return false;
  }

  onClose(): void {
    // this.router.navigate(['', 'details', 'registration-done']);
    const appDomain = ((window as any).environment || environment).AppDomain;
    window.location.href = 'https://'+appDomain+'/login';
  }

  onCloseSidenav(): void {
    this.sidenavContentType = null;
    this.suggestionSidenav.close();
  }

  onAction(item: SuggestionItem): void {
    if (item.titleKey === 'BUSINESS_DETAILS') {
      this.sidenavContentType = 'business-details';
      this.suggestionSidenav.open();
    } else if (item.titleKey === 'WORKING_HOUR') {
      this.sidenavContentType = 'working-hours';
      this.suggestionSidenav.open();
    } else if (item.titleKey === 'Add_Employees') {
      this.sidenavContentType = 'add-employees';
      this.suggestionSidenav.open();
    } else if (item.titleKey === 'Add_Services') {
      this.sidenavContentType = 'add-services';
      this.suggestionSidenav.open();
    } else if (item.titleKey === 'Tax_setup') {
      this.sidenavContentType = 'tax-setup';
      this.suggestionSidenav.open();
    } else {
      this.suggestionSidenav.open();
    }
  }

  get sidenavRegistrationCycleLogId(): number | undefined {
    const regId = this.sharedService.getCookie('RegistrationCycleLogId');
    return regId != null ? +regId : undefined;
  }

  onBusinessDetailsSuccess(): void {
    this.onCloseSidenav();
    this.loadUserData();
  }

  onBusinessDetailsCancel(): void {
    this.onCloseSidenav();
  }

  onWorkingHoursSuccess(): void {
    this.onCloseSidenav();
    this.loadUserData();
  }

  onWorkingHoursCancel(): void {
    this.onCloseSidenav();
  }

  onAddEmployeesSuccess(): void {
    this.onCloseSidenav();
    this.loadUserData();
  }

  onAddEmployeesCancel(): void {
    this.onCloseSidenav();
  }

  onAddServicesSuccess(): void {
    this.onCloseSidenav();
    this.loadUserData();
  }

  onAddServicesCancel(): void {
    this.onCloseSidenav();
  }

  onTaxSetupSuccess(): void {
    this.onCloseSidenav();
    this.loadUserData();
  }

  onTaxSetupCancel(): void {
    this.onCloseSidenav();
  }

  /**
   * Global sidenav footer actions
   * These trigger the corresponding child step actions depending on sidenavContentType.
   */
  onSidenavCancel(): void {
    this.onCloseSidenav();
  }

  onSidenavConfirm(): void {
    switch (this.sidenavContentType) {
      case 'business-details':
        this.businessDetailsCmp?.submit();
        break;
      case 'working-hours':
        this.workingHoursCmp?.onSubmit();
        break;
      case 'add-employees':
        this.addEmployeesCmp?.onSubmit();
        break;
      case 'add-services':
        this.addServicesCmp?.onSubmit();
        break;
      case 'tax-setup':
        this.taxSetupCmp?.onSubmit();
        break;
      default:
        break;
    }
  }

}
